// Hero carousel slide editor for the Home page.
// Reads/writes settings key `hero_slides:home`. Empty array == fall back to built-in defaults.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError } from "../../api";
import { FileUploader } from "../../components/FileUploader";
import { cryptoId } from "../../components/PageBlocks";
import { LabeledInput, LabeledTextarea } from "./_inputs";

const newHeroSlide = () => ({
  id: cryptoId(),
  eyebrow: "",
  title: "",
  subtitle: "",
  image: "",
  video: "",
  cta_label: "",
  cta_href: "",
});

export const HeroSlidesEditor = () => {
  const settingsKey = "hero_slides:home";
  const [slides, setSlides] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get(`/settings/${encodeURIComponent(settingsKey)}`)
      .then((r) => {
        if (!active) return;
        const v = r.data?.value;
        const arr = Array.isArray(v) ? v : [];
        setSlides(arr);
        setSaved(arr);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const dirty = JSON.stringify(slides) !== JSON.stringify(saved);

  const save = async () => {
    setLoading(true);
    setStatus(null);
    try {
      await api.put(`/admin/settings/${encodeURIComponent(settingsKey)}`, { value: slides });
      // Verify by re-reading
      const verify = await api.get(`/settings/${encodeURIComponent(settingsKey)}`);
      const persisted = verify.data?.value;
      const ok = Array.isArray(persisted) && persisted.length === slides.length;
      setSaved(slides);
      setStatus({
        kind: "ok",
        message: ok
          ? `${slides.length} slide${slides.length === 1 ? "" : "s"} saved & verified — live on /`
          : "Save returned 200 but server still shows old data. Retry, or contact support.",
        at: new Date(),
      });
      toast.success("Hero slides saved.");
    } catch (e) {
      const httpStatus = e?.response?.status;
      const detail = formatError(e?.response?.data?.detail);
      const message =
        httpStatus === 401 || httpStatus === 403
          ? "Session expired — log out, log back in, then save again."
          : detail
          ? `Save failed (HTTP ${httpStatus || "?"}): ${detail}`
          : `Save failed: ${e?.message || "Network error"}.`;
      setStatus({ kind: "err", message, at: new Date() });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const addSlide = () => setSlides([...slides, newHeroSlide()]);
  const removeSlide = (id) => setSlides(slides.filter((s) => s.id !== id));
  const updateSlide = (id, patch) =>
    setSlides(slides.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const moveSlide = (id, dir) => {
    const idx = slides.findIndex((s) => s.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= slides.length) return;
    const next = [...slides];
    [next[idx], next[j]] = [next[j], next[idx]];
    setSlides(next);
  };

  return (
    <div className="border-4 border-terra bg-bone shadow-hard p-6 space-y-5" data-testid="hero-slides-editor">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="font-display text-2xl uppercase tracking-tight">Home Hero Carousel</div>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink">
          {slides.length} slide{slides.length === 1 ? "" : "s"}
        </span>
      </div>
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink">
        Rotating hero at the top of the home page. Add slides, reorder them, set image / CTA. Leave empty to use the default 3 slides.
      </p>

      <div className="space-y-4">
        {slides.map((s, i) => (
          <div key={s.id} className="border-2 border-asphalt bg-card-light p-4 space-y-3" data-testid={`slide-card-${i}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] bg-terra text-bone px-2 py-1">
                Slide #{i + 1}
              </span>
              <div className="ml-auto flex gap-1">
                <button type="button" onClick={() => moveSlide(s.id, -1)} disabled={i === 0} className="border-2 border-asphalt px-2 py-1 font-mono text-[10px] disabled:opacity-30" data-testid={`slide-${i}-up`}>↑</button>
                <button type="button" onClick={() => moveSlide(s.id, 1)} disabled={i === slides.length - 1} className="border-2 border-asphalt px-2 py-1 font-mono text-[10px] disabled:opacity-30" data-testid={`slide-${i}-down`}>↓</button>
                <button type="button" onClick={() => removeSlide(s.id)} className="border-2 border-terra bg-terra text-bone px-2 py-1 font-mono text-[10px]" data-testid={`slide-${i}-remove`}>×</button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <LabeledInput label="Eyebrow" value={s.eyebrow} onChange={(v) => updateSlide(s.id, { eyebrow: v })} testid={`slide-${i}-eyebrow`} placeholder="Volume IX" />
              <LabeledInput label="Title" value={s.title} onChange={(v) => updateSlide(s.id, { title: v })} testid={`slide-${i}-title`} placeholder="Wheels West" />
            </div>
            <LabeledTextarea label="Subtitle" rows={2} value={s.subtitle} onChange={(v) => updateSlide(s.id, { subtitle: v })} testid={`slide-${i}-subtitle`} />
            <FileUploader
              accept="image"
              label="Background image"
              value={s.image}
              onChange={(url) => updateSlide(s.id, { image: url })}
              testid={`slide-${i}-image`}
            />
            <div className="grid sm:grid-cols-2 gap-3">
              <LabeledInput label="CTA button label" value={s.cta_label} onChange={(v) => updateSlide(s.id, { cta_label: v })} testid={`slide-${i}-cta-label`} placeholder="Read the field report" />
              <LabeledInput label="CTA URL" value={s.cta_href} onChange={(v) => updateSlide(s.id, { cta_href: v })} testid={`slide-${i}-cta-href`} placeholder="/stories" />
            </div>
          </div>
        ))}
        {slides.length === 0 && (
          <div className="border-2 border-dashed border-asphalt p-6 text-center font-mono text-xs uppercase tracking-[0.25em] text-ink">
            &#10022; Using built-in defaults. Add a slide below to override.
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 pt-3 border-t-2 border-asphalt/30 items-start">
        <button
          type="button"
          onClick={addSlide}
          data-testid="add-slide"
          className="border-2 border-asphalt px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-asphalt hover:text-bone"
        >
          + Add slide
        </button>
        <button
          type="button"
          disabled={loading || !dirty}
          onClick={save}
          data-testid="hero-slides-save"
          className="bg-asphalt text-bone border-2 border-asphalt px-5 py-3 font-display tracking-wider uppercase text-lg shadow-hard hover:bg-terra disabled:opacity-50"
        >
          {loading ? "Saving…" : dirty ? "Save slides" : "All saved"}
        </button>
        {status && (
          <div
            className={`flex-1 min-w-[220px] border-2 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] leading-snug ${
              status.kind === "ok"
                ? "border-asphalt bg-bone text-asphalt"
                : "border-terra bg-terra/10 text-terra"
            }`}
            data-testid="hero-slides-status"
          >
            <div className="text-[10px] opacity-70 mb-0.5">
              {status.at.toLocaleTimeString()} — {status.kind === "ok" ? "✓ saved" : "✗ failed"}
            </div>
            <div className="normal-case tracking-normal text-xs">{status.message}</div>
          </div>
        )}
      </div>
    </div>
  );
};
